import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { $userAccountsStore, $userCardsStore, $userStore } from '@lib/store/userManagement';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { UserType } from '@shared/api/rest/users';
import { RemoveAccountsModal, RemoveCardsModal, UserCards, UserInfo, UserInfoHeader, UserSidebar } from './widgets';
import { AccountTileSmall } from '@pages';
import { AddAccountsModal, AddCardsModal } from '@widgets';
import { Button, NoData, PageContainer, Spinner, Text } from '@shared/widgets';

export const UserPage = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userStore = useUnit($userStore.store);
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showRemoveAccounts, setShowRemoveAccounts] = useState<boolean>(false);
  const [showAddCards, setShowAddCards] = useState<boolean>(false);
  const [showRemoveCards, setShowRemoveCards] = useState<boolean>(false);

  const { t } = useTranslation('user');

  useEffect(() => {
    if (!profileFeatureStore.data?.userManagement?.viewUserProfileDetails) navigate(RoutePaths.users.onNavigate());
  }, [profileFeatureStore.data?.userManagement?.viewUserProfileDetails]);

  useEffect(() => {
    if (!id) return;
    $userStore.events.getUser(id);
    $userAccountsStore.events.getUserAccounts({ AssignedUserId: id });

    return () => {
      $userStore.events.clear();
      $userAccountsStore.events.clear();
    };
  }, [id]);

  const accessAccounts = useMemo(
    () => userStore.data?.userType === UserType.MANAGER && profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );
  const accessCards = useMemo(
    () => userStore.data?.userType === UserType.USER && profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );
  return (
    <PageContainer loading={userStore.loading} title={t('title')} back backText={t('backText')} return>
      <div className={cn('flex justify-center', PaletteUtils.isVcfcPalette && 'max-md:block')}>
        {(!userStore.data || !profileFeatureStore.data?.userManagement?.viewUserProfileDetails) && <NoData />}
        {userStore.data && profileFeatureStore.data?.userManagement?.viewUserProfileDetails && (
          <>
            <UserInfo value={userStore.data}>
              <UserInfoHeader className='mb-12' user={userStore.data} />
              {userStore.data.userType !== UserType.ADMIN && (
                <>
                  {accessAccounts && (
                    <div className='mb-12'>
                      <div className='mb-6 flex flex-wrap items-center justify-between'>
                        <Text className='pr-4' weight='medium' size='2xl'>
                          {t('accounts.title', { count: userAccountsStore.data.length })}
                        </Text>
                        <div className='flex items-center'>
                          {profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowAddAccounts(true)}>
                              {t('buttons.add', { ns: 'common' })}
                            </Button>
                          )}
                          <AddAccountsModal
                            isOpen={showAddAccounts}
                            onClose={() => setShowAddAccounts(false)}
                            user={userStore.data}
                            accountsItems={userAccountsStore.data}
                            onSubmit={(data) => $userAccountsStore.events.addAccounts(data)}
                          />
                          {userAccountsStore.data.length > 0 && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowRemoveAccounts(true)}>
                              {t('buttons.remove', { ns: 'common' })}
                            </Button>
                          )}
                          <RemoveAccountsModal show={showRemoveAccounts} userData={userStore.data} onClose={() => setShowRemoveAccounts(false)} />
                        </div>
                      </div>
                      {userAccountsStore.loading ? (
                        <Spinner />
                      ) : userAccountsStore.data.length > 0 ? (
                        <div className={cn('flex flex-wrap gap-[30px]', userStore.data.locked && 'pointer-events-none select-none opacity-40')}>
                          {userAccountsStore.data.map((item, idx) => (
                            <AccountTileSmall key={idx} className={cn('w-[calc(50%-30px)]', 'max-[1400px]:w-full')} account={item} />
                          ))}
                        </div>
                      ) : (
                        <NoData text={t('noData.noRecords', { ns: 'common' })} />
                      )}
                    </div>
                  )}
                  {accessCards && (
                    <div className='mb-12'>
                      <div className='mb-6 flex flex-wrap items-center justify-between' title={'Cards' + ` (${userCardsStore.data.data.length})`}>
                        <Text className='pr-4' weight='medium' size='2xl'>
                          {t('cards.title', { count: userCardsStore.data.data.length })}
                        </Text>
                        <div className='flex items-center'>
                          <Button color='outlined' className='mr-3' onClick={() => setShowAddCards(true)}>
                            {t('buttons.add', { ns: 'common' })}
                          </Button>
                          <AddCardsModal
                            isOpen={showAddCards}
                            onClose={() => setShowAddCards(false)}
                            user={userStore.data}
                            cardsItems={userCardsStore.data.data}
                            onSubmit={async (data) => $userCardsStore.events.addCards(data)}
                          />
                          {userCardsStore.data.data.length > 0 && (
                            <Button color='outlined' className='mr-3' onClick={() => setShowRemoveCards(true)}>
                              {t('buttons.remove', { ns: 'common' })}
                            </Button>
                          )}
                          <RemoveCardsModal show={showRemoveCards} userData={userStore.data} onClose={() => setShowRemoveCards(false)} />
                        </div>
                      </div>
                      <UserCards userId={userStore.data.id} />
                    </div>
                  )}
                </>
              )}
            </UserInfo>
            <div className={cn('ml-[30px] w-[330px]', 'max-[1400px]:w-[280px]', PaletteUtils.isVcfcPalette && 'max-md:ml-0 max-md:w-full')}>
              {userStore.data.locked && (
                <div className='mb-5 text-center'>
                  <Button color='primary' className='min-w-48' onClick={() => $userStore.events.unlockUser(userStore.data!.id)}>
                    {t('buttons.unlock', { ns: 'common' })}
                  </Button>
                </div>
              )}
              <aside
                className={cn(
                  userStore.data.locked && [
                    'relative flex items-center justify-center p-[15px]',
                    'before:absolute before:left-0 before:z-[40] before:h-full before:w-full before:rounded-sm before:bg-white before:opacity-40',
                  ],
                )}
              >
                <UserSidebar user={userStore.data} />
              </aside>
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};
